import { format, formatDistanceToNow } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { useFleetEvents } from '@/hooks/use-fleet-events';
import { cn } from '@/lib/utils';
import type { FleetEvent } from '@/types/fleet';

import { EventIcon } from './event-icon';
import { EventSourceBadge } from './event-source-badge';
import { EmptyState, ErrorState } from './states';

/**
 * The provision run a `deployed` event records: its dispatch, or the terminal
 * outcome the API's provision reconciler wrote for it (OBV-0058). Without this
 * every run read as a bare "deployed", whether it finished or was abandoned.
 */
const PROVISION_LABELS: Record<string, { label: string; className: string }> = {
  dispatched: { label: 'Provision dispatched', className: 'bg-blue-100 text-blue-800' },
  succeeded: { label: 'Provision succeeded', className: 'bg-emerald-100 text-emerald-800' },
  failed: { label: 'Provision failed', className: 'bg-red-100 text-red-800' },
  abandoned: { label: 'Provision abandoned', className: 'bg-amber-100 text-amber-800' },
};

function provisioningOf(event: FleetEvent): string | undefined {
  const value = event.payload.provisioning;

  return event.event_type === 'deployed' && typeof value === 'string' ? value : undefined;
}

function EventRow({ event }: { event: FleetEvent }): JSX.Element {
  const occurred = new Date(event.occurred_at);
  const provisioning = provisioningOf(event);
  const provisionLabel = provisioning ? PROVISION_LABELS[provisioning] : undefined;

  return (
    <li className="flex gap-3 py-2">
      <div className="mt-0.5">
        <EventIcon eventType={event.event_type} provisioning={provisioning} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium capitalize">
            {event.event_type.replace('_', ' ')}
          </span>
          {provisionLabel && (
            <span
              data-outcome={provisioning}
              className={cn(
                'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
                provisionLabel.className,
              )}
            >
              {provisionLabel.label}
            </span>
          )}
          <EventSourceBadge source={event.source} />
          <span
            className="font-mono text-xs text-muted-foreground"
            title={format(occurred, 'yyyy-MM-dd HH:mm:ss')}
          >
            {formatDistanceToNow(occurred, { addSuffix: true })}
          </span>
        </div>
        {Object.keys(event.payload).length > 0 && (
          <Collapsible className="mt-1">
            <CollapsibleTrigger className="inline-flex items-center gap-0.5 font-mono text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground">
              <ChevronRight className="h-3 w-3" aria-hidden />
              payload
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-1 overflow-auto rounded-sm border border-border bg-secondary/40 p-2 font-mono text-xs">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </li>
  );
}

export function EventTimeline({ serverName }: { serverName: string }): JSX.Element {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFleetEvents(serverName);

  // Infinite-scroll: a sentinel at the list bottom drives fetchNextPage().
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || !hasNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '120px' },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-11 w-full rounded-sm" />
        ))}
      </div>
    );
  }

  if (isError) {
    return <ErrorState title="Couldn't load events." error={error} onRetry={() => void refetch()} />;
  }

  const events = data?.pages.flatMap((p) => p.items) ?? [];

  if (events.length === 0) {
    return <EmptyState message="No events recorded" />;
  }

  return (
    <div>
      <ul className="divide-y divide-border">
        {events.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </ul>
      {hasNextPage && (
        <>
          {/* IntersectionObserver sentinel — auto-loads the next page. */}
          <div ref={sentinelRef} aria-hidden className="h-px w-full" />
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </>
      )}
    </div>
  );
}
